import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Bolt as BoltIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  Cached as RenewIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helper';
import api from '../../services/api';

const PROFORMA_ACTIONS = [
  { value: 'upgrade', label: 'Upgrade to Pro', Icon: BoltIcon },
  { value: 'add_user', label: 'Add Users', Icon: PeopleIcon },
  { value: 'add_credits', label: 'Add Credits', Icon: CreditCardIcon },
  { value: 'renew', label: 'Renew Subscription', Icon: RenewIcon },
];

const BILLING_CYCLE_DISCOUNT_PCT = { 3: 0, 6: 10, 12: 15 };

function getCustomerPlanDisplay(customer) {
  if (!customer) return '';
  const plan = (customer.subscriptionPlan || customer.subscriptionName || '').toString().toLowerCase();
  const remainingDays = customer.subscriptionExpiry?.remainingDays ?? null;
  const isPro = plan.includes('pro');
  const isExpired = remainingDays !== null && remainingDays <= 0;
  if (plan.includes('free')) return 'Free Plan';
  if (isPro && isExpired) return 'Expired Pro Plan';
  if (isPro) return 'Pro Plan';
  return customer.subscriptionPlan || customer.subscriptionName || 'Unknown';
}

function getAllowedActionsForCustomer(customer) {
  if (!customer) return [];
  const plan = (customer.subscriptionPlan || customer.subscriptionName || '').toString().toLowerCase();
  const remainingDays = customer.subscriptionExpiry?.remainingDays ?? null;
  const isPro = plan.includes('pro');
  const isExpired = remainingDays !== null && remainingDays <= 0;
  if (isPro && !isExpired) return ['add_user', 'add_credits'];
  if (isPro && isExpired) return ['renew'];
  return ['upgrade'];
}

const CreateInvoice = ({ 
  open, 
  onClose, 
  onSave, 
  preSelectedCustomer = null, 
  customers = [],
  invoice = null 
}) => {
  const [formData, setFormData] = useState({
    customer: preSelectedCustomer || null,
  });
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Proforma flow state
  const [proformaAction, setProformaAction] = useState('');
  const [unitsData, setUnitsData] = useState(null);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState(null);
  // Numeric fields may be number or '' so user can clear and type (e.g. 1 -> clear -> 12)
  const [proformaForm, setProformaForm] = useState({
    billing_cycle: 3,
    total_user: 1,
    additional_credits: 0,
    slots: 1,
    credits: 1000,
    admin_discount_pct: 0,
  });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [resultInvoice, setResultInvoice] = useState(null);
  const [resultPaymentLink, setResultPaymentLink] = useState(null);

  // When customer changes, clear action if not allowed
  useEffect(() => {
    if (!formData.customer || !proformaAction) return;
    const allowed = getAllowedActionsForCustomer(formData.customer);
    if (allowed.length && !allowed.includes(proformaAction)) {
      setProformaAction('');
      setUnitsData(null);
      setUnitsError(null);
    }
  }, [formData.customer?.id]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({ customer: preSelectedCustomer || null });
      setErrors({});
      setProformaAction('');
      setUnitsData(null);
      setUnitsError(null);
      setProformaForm({ billing_cycle: 3, total_user: 1, additional_credits: 0, slots: 1, credits: 1000, admin_discount_pct: 0 });
      setConfirmError(null);
      setResultInvoice(null);
      setResultPaymentLink(null);
    }
  }, [open, preSelectedCustomer]);

  // Fetch pricing units when customer + action selected
  useEffect(() => {
    if (!open || !formData.customer?.id || !proformaAction) {
      setUnitsData(null);
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    api.get(`/account/${formData.customer.id}/billing/pricing-units/${proformaAction}/`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data?.units) {
          setUnitsData(res.data.units);
          const u = res.data.units;
          if (proformaAction === 'upgrade' && u.current_active_user != null) {
            setProformaForm(prev => ({ ...prev, total_user: Math.max(1, Number(u.current_active_user)) }));
          }
          if (proformaAction === 'renew' && u.total_user_slots != null) {
            setProformaForm(prev => ({ ...prev, total_user: Math.max(1, Number(u.total_user_slots)) }));
          }
        } else {
          setUnitsError(res.data?.error || 'Failed to load pricing');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setUnitsError(err.response?.data?.error || err.message || 'Failed to load pricing');
        setUnitsData(null);
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, formData.customer?.id, proformaAction]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Live price breakdown — aligned with UpgradeToProPage.js and backend (proforma_confirm):
  // Upgrade/Renew: seat cost = totalUser × perUser × months; billing cycle discount on SEATS only;
  // additional credits at per_credit (no cycle discount); then admin discount on (discounted seats + credits).
  const proformaPriceSummary = useMemo(() => {
    if (!unitsData || !proformaAction) return null;
    const u = unitsData;
    const taxRate = Number(u.tax_rate) || 0;
    const currency = u.currency || 'INR';
    const baseCreditsPerUser = Number(u.base_voice_credits_per_cycle) || 1000;
    const adminDiscountPct = Math.min(100, Math.max(0, Number(proformaForm.admin_discount_pct) || 0)) / 100;

    if (proformaAction === 'upgrade' || proformaAction === 'renew') {
      const months = Number(proformaForm.billing_cycle) || 3;
      const totalUser = Math.max(1, Number(proformaForm.total_user) || 1);
      const additionalCredits = Math.max(0, Number(proformaForm.additional_credits) || 0);
      const perUser = Number(u.per_user_cost) || 0;
      const perCredit = Number(u.per_credit_cost) || 0;
      const discountPct = BILLING_CYCLE_DISCOUNT_PCT[months] != null ? BILLING_CYCLE_DISCOUNT_PCT[months] : 0;
      const discountDecimal = discountPct / 100;

      // 1) Base: seat cost (same as UpgradeToProPage baseTotalUser)
      const seatCost = Math.round(totalUser * perUser * months * 100) / 100;
      const additionalCreditsCost = Math.round(additionalCredits * perCredit * 100) / 100;
      const includedFreeCredits = totalUser * baseCreditsPerUser * months;

      // 2) Original subtotal (before any discount)
      const subtotalBeforeDiscount = Math.round((seatCost + additionalCreditsCost) * 100) / 100;

      // 3) Billing cycle discount on SEATS only
      const billingCycleDiscountAmount = Math.round(seatCost * discountDecimal * 100) / 100;

      // 4) Admin discount on ORIGINAL subtotal (so both discounts sum; then apply to original price)
      const adminDiscountAmount = Math.round(subtotalBeforeDiscount * adminDiscountPct * 100) / 100;

      // 5) Sum discounts and subtract from original price before tax
      const totalDiscount = Math.round((billingCycleDiscountAmount + adminDiscountAmount) * 100) / 100;
      const finalSubtotal = Math.round((subtotalBeforeDiscount - totalDiscount) * 100) / 100;
      const seatCostAfterBillingDiscount = Math.round((seatCost - billingCycleDiscountAmount) * 100) / 100;

      // 6) Tax and total
      const taxAmount = Math.round(finalSubtotal * taxRate * 100) / 100;
      const total = Math.round((finalSubtotal + taxAmount) * 100) / 100;

      return {
        seatCost,
        seatCostAfterBillingDiscount,
        includedFreeCredits,
        additionalCreditsCost,
        billingCycleDiscountPct: discountPct,
        billingCycleDiscountAmount,
        subtotalBeforeDiscount,
        totalDiscount,
        subtotal: finalSubtotal,
        discountAmount: billingCycleDiscountAmount,
        adminDiscountAmount,
        afterDiscount: finalSubtotal,
        taxAmount,
        total,
        taxRate,
        currency,
      };
    }

    if (proformaAction === 'add_user') {
      const slots = Math.max(1, Number(proformaForm.slots) || 1);
      const perUser = Number(u.effective_per_user_cost ?? u.per_user_cost) || 0;
      
      // Step 1: Calculate seat cost
      const seatCost = Math.round(slots * perUser * 100) / 100;
      const freeCreditsPerSeat = Number(u.effective_voice_credits_per_cycle ?? u.base_voice_credits_per_cycle) || 1000;
      
      // Step 2: Apply admin discount
      const adminDiscountAmount = Math.round(seatCost * adminDiscountPct * 100) / 100;
      
      // Step 3: Calculate subtotal after discount
      const finalSubtotal = Math.round((seatCost - adminDiscountAmount) * 100) / 100;
      
      // Step 4: Calculate tax
      const taxAmount = Math.round(finalSubtotal * taxRate * 100) / 100;
      
      // Step 5: Calculate total
      const total = Math.round((finalSubtotal + taxAmount) * 100) / 100;

      return {
        seatCost,
        includedFreeCredits: Math.floor(slots * freeCreditsPerSeat),
        subtotalBeforeDiscount: seatCost,
        totalDiscount: adminDiscountAmount,
        subtotal: finalSubtotal,
        discountAmount: 0,
        adminDiscountAmount,
        afterDiscount: finalSubtotal,
        taxAmount,
        total,
        taxRate,
        currency,
      };
    }

    if (proformaAction === 'add_credits') {
      const credits = Math.max(1000, Number(proformaForm.credits) || 1000);
      const perCredit = Number(u.effective_per_credit_cost ?? u.per_credit_cost) || 0;
      
      // Step 1: Calculate base cost
      const baseCost = Math.round(credits * perCredit * 100) / 100;
      
      // Step 2: Apply admin discount
      const adminDiscountAmount = Math.round(baseCost * adminDiscountPct * 100) / 100;
      
      // Step 3: Calculate subtotal after discount
      const finalSubtotal = Math.round((baseCost - adminDiscountAmount) * 100) / 100;
      
      // Step 4: Calculate tax
      const taxAmount = Math.round(finalSubtotal * taxRate * 100) / 100;
      
      // Step 5: Calculate total
      const total = Math.round((finalSubtotal + taxAmount) * 100) / 100;

      return {
        subtotalBeforeDiscount: baseCost,
        totalDiscount: adminDiscountAmount,
        subtotal: finalSubtotal,
        discountAmount: 0,
        adminDiscountAmount,
        afterDiscount: finalSubtotal,
        taxAmount,
        total,
        taxRate,
        currency,
      };
    }

    return null;
  }, [unitsData, proformaAction, proformaForm]);

  const validateProforma = () => {
    const err = {};
    if (!formData.customer?.id) err.customer = 'Select a customer';
    if (!proformaAction) err.action = 'Select an action';
    // Use effective values (empty => default); validate only when user typed something invalid
    const additionalCreditsNum = Number(proformaForm.additional_credits);
    if (proformaAction === 'upgrade') {
      if (!unitsData?.plan_id) err.units = 'Pricing not loaded';
      if (proformaForm.total_user !== '' && proformaForm.total_user != null && (Number.isNaN(Number(proformaForm.total_user)) || Number(proformaForm.total_user) < 1)) err.total_user = 'Total users must be at least 1';
      if (![3, 6, 12].includes(Number(proformaForm.billing_cycle))) err.billing_cycle = 'Invalid billing cycle';
      if (proformaForm.additional_credits !== '' && proformaForm.additional_credits != null && (Number.isNaN(additionalCreditsNum) || additionalCreditsNum < 0)) err.additional_credits = 'Invalid credits';
    }
    if (proformaAction === 'add_user') {
      if (proformaForm.slots !== '' && proformaForm.slots != null && (Number.isNaN(Number(proformaForm.slots)) || Number(proformaForm.slots) < 1)) err.slots = 'Seats must be at least 1';
    }
    if (proformaAction === 'add_credits') {
      if (proformaForm.credits !== '' && proformaForm.credits != null && (Number.isNaN(Number(proformaForm.credits)) || Number(proformaForm.credits) < 1000)) err.credits = 'Minimum 1000 credits';
    }
    if (proformaAction === 'renew') {
      if (proformaForm.total_user !== '' && proformaForm.total_user != null && (Number.isNaN(Number(proformaForm.total_user)) || Number(proformaForm.total_user) < 1)) err.total_user = 'Total users must be at least 1';
      if (![3, 6, 12].includes(Number(proformaForm.billing_cycle))) err.billing_cycle = 'Invalid billing cycle';
      if (proformaForm.additional_credits !== '' && proformaForm.additional_credits != null && (Number.isNaN(additionalCreditsNum) || additionalCreditsNum < 0)) err.additional_credits = 'Invalid credits';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const buildProformaPayload = () => {
    const accountId = formData.customer?.id;
    if (!accountId || !proformaAction) return null;
    // Admin flow sends only percentage; final discount = billing_cycle discount + admin discount
    const adminDiscountPercentage = Math.min(100, Math.max(0, Number(proformaForm.admin_discount_pct) || 0));
    const base = {
      action: proformaAction,
      ...(adminDiscountPercentage > 0 ? { admin_discount_percentage: adminDiscountPercentage } : {}),
    };
    if (proformaAction === 'upgrade') {
      return {
        ...base,
        plan_id: unitsData?.plan_id,
        total_user: Math.max(1, Number(proformaForm.total_user) || 1),
        additional_credits: Math.max(0, Number(proformaForm.additional_credits) || 0),
        billing_cycle: Number(proformaForm.billing_cycle) || 3,
      };
    }
    if (proformaAction === 'add_user') {
      return { ...base, slots: Math.max(1, Number(proformaForm.slots) || 1) };
    }
    if (proformaAction === 'add_credits') {
      return { ...base, credits: Math.max(1000, Number(proformaForm.credits) || 1000) };
    }
    if (proformaAction === 'renew') {
      return {
        ...base,
        total_user: Math.max(1, Number(proformaForm.total_user) || 1),
        additional_credits: Math.max(0, Number(proformaForm.additional_credits) || 0),
        billing_cycle: Number(proformaForm.billing_cycle) || 3,
      };
    }
    return null;
  };

  const handleCreateProformaInvoice = async () => {
    if (!validateProforma()) return;
    const payload = buildProformaPayload();
    if (!payload) {
      setSnackbar({ open: true, message: 'Invalid payload', severity: 'error' });
      return;
    }
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      const res = await api.post(`/account/${formData.customer.id}/billing/confirm/`, payload);
      const status = res?.status;
      const isSuccess = (status === 200 || status === 201) && res.data?.success;
      if (isSuccess) {
        setResultInvoice(res.data.invoice || { invoice_number: res.data.invoice_number });
        setResultPaymentLink(res.data.payment_link || res.data.payment_url || '');
        setSnackbar({ open: true, message: 'Invoice created. Share the payment link with the customer.', severity: 'success' });
        if (onSave) await onSave(res.data);
        onClose();
        window.location.reload();
      } else {
        setConfirmError(res.data?.error || res.data?.detail || 'Failed to create invoice');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to create invoice';
      setConfirmError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptIcon color="primary" sx={{ fontSize: 24 }} />
            <Typography variant="h6" fontWeight="bold">
              Create Proforma Invoice
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, px: 2, width: '100%', maxWidth: '100%', overflow: 'auto' }}>
          <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
            <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
              {/* Step 1: Select Client */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>1</Box>
                <Typography variant="subtitle2" fontWeight="600">Select client</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                Choose the account this invoice is for. Plan and billing context will load automatically.
              </Typography>
              <Autocomplete
                value={formData.customer}
                onChange={(_, value) => handleInputChange('customer', value)}
                options={customers}
                getOptionLabel={(opt) => opt?.companyName || ''}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search or select client..."
                    error={!!errors.customer}
                    helperText={errors.customer}
                  />
                )}
                renderOption={(props, option) => {
                  const planLabel = getCustomerPlanDisplay(option);
                  const isFree = planLabel === 'Free Plan';
                  const isExpiredPro = planLabel === 'Expired Pro Plan';
                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, width: '100%' }}>
                        <Avatar sx={{ bgcolor: isFree ? 'warning.main' : isExpiredPro ? 'error.main' : 'primary.main', width: 40, height: 40 }}>
                          <BusinessIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight="medium">{option.companyName}</Typography>
                            <Chip label={planLabel} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} color={isFree ? 'warning' : isExpiredPro ? 'error' : 'primary'} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">{option.ownerName} • {option.ownerEmail}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">GSTIN: {option.businessInfo?.gstin || 'Not provided'}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{option.businessInfo?.address || 'Address not provided'}</Typography>
                        </Box>
                      </Box>
                    </li>
                  );
                }}
              />

              {/* Empty state: no client selected */}
              {!formData.customer && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, px: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'grey.300' }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                    <PersonIcon sx={{ fontSize: 28, color: 'grey.500' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" textAlign="center" fontWeight={500}>
                    Select a client above to begin.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>
                    Action: Search or pick from the dropdown, then choose an invoice type in step 2.
                  </Typography>
                </Box>
              )}

              {/* Step 2: Select Action — only when client selected */}
              {formData.customer && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 0.5 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>2</Box>
                    <Typography variant="subtitle2" fontWeight="600">Choose invoice type</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Client is on <strong>{getCustomerPlanDisplay(formData.customer)}</strong>. Click an option below to create the right invoice.
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {PROFORMA_ACTIONS.map((opt) => {
                      const Icon = opt.Icon;
                      const allowed = getAllowedActionsForCustomer(formData.customer).includes(opt.value);
                      const selected = proformaAction === opt.value;
                      const actionHint = { upgrade: 'Free → Pro', add_user: 'More seats (prorated)', add_credits: 'Voice credits only', renew: 'Extend current plan' }[opt.value] || '';

                      return (
                        <Grid item xs={12} sm={6} key={opt.value}>
                          <Card
                            onClick={allowed ? () => setProformaAction(opt.value) : undefined}
                            sx={{
                              height: 120,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              cursor: allowed ? 'pointer' : 'not-allowed',
                              border: '2px solid',
                              borderColor: selected ? 'primary.main' : allowed ? 'grey.300' : 'grey.200',
                              bgcolor: selected ? 'primary.50' : allowed ? 'background.paper' : 'grey.100',
                              opacity: allowed ? 1 : 0.6,
                              transition: 'all 0.2s ease',
                              '&:hover': allowed ? { borderColor: 'primary.main', bgcolor: selected ? 'primary.50' : 'grey.50' } : {},
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center' }}>
                              <Icon sx={{ fontSize: 30, mb: 0.5, color: selected ? 'primary.main' : allowed ? 'grey.600' : 'grey.400' }} />
                              <Typography variant="body2" fontWeight={selected ? 600 : 500} color={allowed ? 'text.primary' : 'text.secondary'}>
                                {opt.label}
                              </Typography>
                              {actionHint && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                  {actionHint}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {proformaAction && (
                    <>
                      {/* Loading */}
                      {unitsLoading && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2">Loading pricing…</Typography>
                        </Box>
                      )}

                      {/* Error */}
                      {unitsError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUnitsError(null)}>
                          {unitsError}
                        </Alert>
                      )}

                      {/* Step 3: Pricing & configuration */}
                      {unitsData && !unitsLoading && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1.5 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>3</Box>
                            <Typography variant="subtitle2" fontWeight="600">Review pricing & configure</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                            Rates below are from the client&apos;s account. Adjust options as needed, then generate the proforma to get a payment link.
                          </Typography>
                        </>
                      )}

                      {/* ===== COST STRIP ===== */}
                      {unitsData && !unitsLoading && (
                        <Card variant="outlined" sx={{ mb: 3, bgcolor: "grey.50", borderRadius: 2, border: 'none' }}>
                          <CardContent sx={{ py: 2.5, px: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mb: 2 }}>
                              Customer&apos;s current rates
                            </Typography>
                            <Grid container spacing={3}>
                              {/* Show appropriate pricing info based on action */}
                              {(proformaAction === "upgrade" || proformaAction === "renew" || proformaAction === "add_user") && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: "flex", alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ 
                                      width: 48, 
                                      height: 48, 
                                      borderRadius: '12px', 
                                      bgcolor: 'rgba(59, 130, 246, 0.1)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <PersonIcon sx={{ color: 'rgb(59, 130, 246)', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                        {proformaAction === "add_user" ? 'Per User Cost (effective)' : 'Per User Cost'}
                                      </Typography>
                                      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25 }}>
                                        {formatCurrency(
                                          proformaAction === "add_user"
                                            ? (unitsData.effective_per_user_cost ?? unitsData.per_user_cost)
                                            : unitsData.per_user_cost,
                                          unitsData.currency || "INR"
                                        )}
                                      </Typography>
                                      {proformaAction === "add_user" && unitsData.remaining_days != null && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                                          Prorated for {unitsData.remaining_days} remaining days in cycle
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {(proformaAction === "upgrade" || proformaAction === "renew" || proformaAction === "add_credits") && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: "flex", alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ 
                                      width: 48, 
                                      height: 48, 
                                      borderRadius: '12px', 
                                      bgcolor: 'rgba(168, 85, 247, 0.1)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <CreditCardIcon sx={{ color: 'rgb(168, 85, 247)', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                        Per Credit Cost
                                      </Typography>
                                      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25 }}>
                                        {formatCurrency(
                                          proformaAction === "add_credits"
                                            ? (unitsData.effective_per_credit_cost ?? unitsData.per_credit_cost)
                                            : unitsData.per_credit_cost,
                                          unitsData.currency || "INR"
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {(proformaAction === "upgrade" || proformaAction === "renew" || proformaAction === "add_user") && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: "flex", alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ 
                                      width: 48, 
                                      height: 48, 
                                      borderRadius: '12px', 
                                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <BoltIcon sx={{ color: 'rgb(16, 185, 129)', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                        {proformaAction === "add_user" ? 'Free Credits/User (effective)' : 'Free Credits/User'}
                                      </Typography>
                                      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25 }}>
                                        {(proformaAction === "add_user"
                                          ? (unitsData.effective_voice_credits_per_cycle ?? unitsData.base_voice_credits_per_cycle)
                                          : (unitsData.base_voice_credits_per_cycle ?? 1000)
                                        ).toLocaleString()}
                                      </Typography>
                                      {proformaAction === "add_user" && unitsData.remaining_days != null && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                                          Based on {unitsData.remaining_days} remaining days
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {/* Current billing cycle — add_user, renew, add_credits */}
                              {(proformaAction === "add_user" || proformaAction === "renew" || proformaAction === "add_credits") && (
                                <Grid item xs={12}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap',
                                    alignItems: 'center', 
                                    gap: 2, 
                                    py: 1.5, 
                                    px: 2, 
                                    borderRadius: '10px', 
                                    bgcolor: 'rgba(0, 0, 0, 0.04)', 
                                    border: '1px solid rgba(0, 0, 0, 0.06)' 
                                  }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                      Client&apos;s current billing cycle
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color="text.primary" component="span">
                                      {proformaAction === "add_user" && (unitsData.billing_cycle_months != null || unitsData.remaining_days != null) && (
                                        <>
                                          {unitsData.billing_cycle_months != null ? `${unitsData.billing_cycle_months} months` : ''}
                                          {unitsData.billing_cycle_months != null && unitsData.remaining_days != null ? ' • ' : ''}
                                          {unitsData.remaining_days != null ? `${unitsData.remaining_days} days remaining` : ''}
                                        </>
                                      )}
                                      {proformaAction === "renew" && (unitsData.billing_cycle_months != null || unitsData.billing_cycle_days != null) && (
                                        <>
                                          {unitsData.billing_cycle_months != null ? `${unitsData.billing_cycle_months} months` : (unitsData.billing_cycle_days != null ? `${unitsData.billing_cycle_days} days` : '')}
                                          {unitsData.current_plan ? ` (${unitsData.current_plan})` : ''}
                                        </>
                                      )}
                                      {proformaAction === "add_credits" && (
                                        <>Pro plan • per-credit rate as above</>
                                      )}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      )}

                      {/* ===== FORM SECTION ===== */}
                      {unitsData && !unitsLoading && (
                        <>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mt: 2, mb: 1.5 }}>
                            Configure options
                          </Typography>
                          <Grid container spacing={3}>
                            {/* UPGRADE / RENEW */}
                            {(proformaAction === "upgrade" || proformaAction === "renew") && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Billing Cycle
                                  </Typography>
                                  <FormControl fullWidth>
                                    <Select
                                      value={proformaForm.billing_cycle}
                                      onChange={(e) => setProformaForm(prev => ({ ...prev, billing_cycle: Number(e.target.value) }))}
                                      sx={{ 
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'rgba(0, 0, 0, 0.12)'
                                        }
                                      }}
                                    >
                                      <MenuItem value={3}>3 Months</MenuItem>
                                      <MenuItem value={6}>6 Months</MenuItem>
                                      <MenuItem value={12}>1 Year</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {BILLING_CYCLE_DISCOUNT_PCT[proformaForm.billing_cycle] > 0 && (
                                    <Typography variant="caption" sx={{ color: 'rgb(16, 185, 129)', fontWeight: 600, display: 'block', mt: 1 }}>
                                      % Billing Cycle Discount: {BILLING_CYCLE_DISCOUNT_PCT[proformaForm.billing_cycle]}% applied
                                    </Typography>
                                  )}
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Total Users
                                  </Typography>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.total_user === '' || proformaForm.total_user == null ? '' : proformaForm.total_user}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, total_user: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, total_user: n }));
                                    }}
                                    inputProps={{ min: 1 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Additional Credits
                                  </Typography>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.additional_credits === '' || proformaForm.additional_credits == null ? '' : proformaForm.additional_credits}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, additional_credits: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, additional_credits: n }));
                                    }}
                                    inputProps={{ min: 0 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Admin Discount %
                                  </Typography>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.admin_discount_pct === '' || proformaForm.admin_discount_pct == null ? '' : proformaForm.admin_discount_pct}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, admin_discount_pct: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, admin_discount_pct: Math.min(100, n) }));
                                    }}
                                    placeholder="0"
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>
                              </>
                            )}

                            {/* ADD USERS */}
                            {proformaAction === "add_user" && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Seats to Add
                                  </Typography>
                                  {unitsData.remaining_days != null && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                      Charges prorated for {unitsData.remaining_days} remaining days in current cycle (effective cost/credits as above).
                                    </Typography>
                                  )}
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.slots === '' || proformaForm.slots == null ? '' : proformaForm.slots}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, slots: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, slots: n }));
                                    }}
                                    inputProps={{ min: 1 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Admin Discount %
                                  </Typography>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.admin_discount_pct === '' || proformaForm.admin_discount_pct == null ? '' : proformaForm.admin_discount_pct}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, admin_discount_pct: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, admin_discount_pct: Math.min(100, n) }));
                                    }}
                                    placeholder="0"
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>
                              </>
                            )}

                            {/* ADD CREDITS */}
                            {proformaAction === "add_credits" && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Number of Credits
                                  </Typography>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.credits === '' || proformaForm.credits == null ? '' : proformaForm.credits}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, credits: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, credits: n }));
                                    }}
                                    inputProps={{ min: 1000 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                                    Admin Discount %
                                  </Typography>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    value={proformaForm.admin_discount_pct === '' || proformaForm.admin_discount_pct == null ? '' : proformaForm.admin_discount_pct}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') { setProformaForm(prev => ({ ...prev, admin_discount_pct: '' })); return; }
                                      const n = parseInt(v, 10);
                                      if (!Number.isNaN(n) && n >= 0) setProformaForm(prev => ({ ...prev, admin_discount_pct: Math.min(100, n) }));
                                    }}
                                    placeholder="0"
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>

                          {/* ===== PRICING BREAKDOWN ===== */}
                          {proformaPriceSummary && (
                            <Box sx={{ mt: 4 }}>
                              <Box sx={{ 
                                bgcolor: 'white',
                                borderRadius: '12px',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                overflow: 'hidden'
                              }}>
                                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                                  <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="span" sx={{ 
                                      width: 8, 
                                      height: 8, 
                                      borderRadius: '50%', 
                                      bgcolor: 'rgb(59, 130, 246)' 
                                    }} />
                                    Pricing Breakdown
                                  </Typography>
                                </Box>

                                <Box sx={{ px: 3, py: 2.5 }}>
                                  {/* 1. Line items */}
                                  {(proformaAction === 'upgrade' || proformaAction === 'renew') && (
                                    <>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                          <Typography variant="body2" fontWeight={600} color="text.primary">
                                            Seat Cost
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" sx={{ mt: 0.5 }}>
                                            Includes {proformaPriceSummary.includedFreeCredits?.toLocaleString()} Free Credits{' '}
                                            <Chip label="FREE" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(16, 185, 129, 0.1)', color: 'rgb(16, 185, 129)', border: 'none' }} />
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={600}>
                                          {formatCurrency(proformaPriceSummary.seatCost, proformaPriceSummary.currency)}
                                        </Typography>
                                      </Box>
                                      {(proformaPriceSummary.additionalCreditsCost ?? 0) > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                          <Typography variant="body2" fontWeight={600} color="text.primary">
                                            Additional Credits
                                          </Typography>
                                          <Typography variant="body1" fontWeight={600}>
                                            {formatCurrency(proformaPriceSummary.additionalCreditsCost, proformaPriceSummary.currency)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </>
                                  )}

                                  {proformaAction === 'add_user' && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                      <Box>
                                        <Typography variant="body2" fontWeight={600} color="text.primary">
                                          Seat Cost
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" sx={{ mt: 0.5 }}>
                                          Effective cost × seats{unitsData?.remaining_days != null ? ` (prorated for ${unitsData.remaining_days} days remaining)` : ''}. Includes {proformaPriceSummary.includedFreeCredits?.toLocaleString()} Free Credits{' '}
                                          <Chip label="FREE" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(16, 185, 129, 0.1)', color: 'rgb(16, 185, 129)', border: 'none' }} />
                                        </Typography>
                                      </Box>
                                      <Typography variant="body1" fontWeight={600}>
                                        {formatCurrency(proformaPriceSummary.seatCost, proformaPriceSummary.currency)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {proformaAction === 'add_credits' && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                      <Typography variant="body2" fontWeight={600} color="text.primary">
                                        Voice Credits
                                      </Typography>
                                      <Typography variant="body1" fontWeight={600}>
                                        {formatCurrency(proformaPriceSummary.subtotalBeforeDiscount, proformaPriceSummary.currency)}
                                      </Typography>
                                    </Box>
                                  )}

                                  <Divider sx={{ my: 2 }} />

                                  {/* 2. Subtotal */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="body2" fontWeight={600} color="text.primary">
                                      Subtotal
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                      {formatCurrency(proformaPriceSummary.subtotalBeforeDiscount, proformaPriceSummary.currency)}
                                    </Typography>
                                  </Box>

                                  {/* 3. Discount (label clarifies Billing cycle + Admin; generated invoice shows single discount amount) */}
                                  {(() => {
                                    const hasBilling = (proformaPriceSummary.billingCycleDiscountAmount ?? 0) > 0;
                                    const hasAdmin = (proformaPriceSummary.adminDiscountAmount ?? 0) > 0;
                                    const discountLabel = (proformaAction === 'upgrade' || proformaAction === 'renew')
                                      ? (hasBilling && hasAdmin ? 'Discount (Billing cycle + Admin)' : hasBilling ? 'Discount (Billing cycle)' : hasAdmin ? 'Discount (Admin)' : 'Discount')
                                      : (hasAdmin ? 'Discount (Admin)' : 'Discount');
                                    return (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          {discountLabel}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600} sx={{ color: (proformaPriceSummary.totalDiscount ?? 0) > 0 ? 'rgb(16, 185, 129)' : undefined }}>
                                          {(proformaPriceSummary.totalDiscount ?? 0) > 0 ? `-${formatCurrency(proformaPriceSummary.totalDiscount, proformaPriceSummary.currency)}` : formatCurrency(0, proformaPriceSummary.currency)}
                                        </Typography>
                                      </Box>
                                    );
                                  })()}

                                  {/* 4. Amount before tax */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="body2" fontWeight={600} color="text.primary">
                                      Amount before tax
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                      {formatCurrency(proformaPriceSummary.subtotal ?? proformaPriceSummary.afterDiscount, proformaPriceSummary.currency)}
                                    </Typography>
                                  </Box>

                                  {/* 5. Tax */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Tax ({(proformaPriceSummary.taxRate * 100).toFixed(0)}% GST)
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                      {formatCurrency(proformaPriceSummary.taxAmount, proformaPriceSummary.currency)}
                                    </Typography>
                                  </Box>

                                  <Divider sx={{ my: 2 }} />

                                  {/* 6. Total payable */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" fontWeight={700} color="text.primary">
                                      Total Payable
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ color: 'rgb(16, 185, 129)' }}>
                                      {formatCurrency(proformaPriceSummary.total, proformaPriceSummary.currency)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', pt: 2, px: 3, pb: 3, flexDirection: 'column', alignItems: 'stretch', gap: 2, bgcolor: '#fafafa' }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-start' }}>
            After generating: An invoice and Razorpay payment link will be created. Share the link with the client to collect payment.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: '8px',
              px: 4,
              py: 1.25,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.4)',
                bgcolor: 'rgba(0, 0, 0, 0.02)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateProformaInvoice}
            disabled={!formData.customer || !proformaAction || !unitsData || confirmLoading || !!resultInvoice}
            startIcon={confirmLoading ? <CircularProgress size={18} color="inherit" /> : null}
            size="large"
            sx={{ 
              borderRadius: '8px',
              px: 4,
              py: 1.25,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: 'rgb(37, 99, 235)',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'rgb(29, 78, 216)',
                boxShadow: 'none'
              },
              '&:disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {confirmLoading ? 'Creating…' : resultInvoice ? 'Proforma Created' : 'Generate Proforma'}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateInvoice;
